import {
   array,
   boolean,
   description,
   isoTimestamp,
   maxLength,
   nullable,
   number,
   object,
   optional,
   picklist,
   pipe,
   string,
   type InferOutput,
} from 'valibot'

import { hashedUserIdSchema } from '../measurements/schema'

/**
 * Valibot schemas for the `/v1/weights` delta-sync API. An entry is identified
 * by its `timestamp`, the same key the legacy `/v1/sync` weight history uses, so
 * both paths agree on which entry is which. Range and future-time rules live in
 * the Worker's weight service.
 */

export const MAX_WEIGHT_ENTRIES_PER_REQUEST = 200
/** How long weight tombstones are returned to clients; matches measurements. */
export const WEIGHT_TOMBSTONE_RETENTION_MS = 90 * 24 * 60 * 60 * 1000

const isoDateTimeSchema = pipe(string(), isoTimestamp())

export const weightEntrySchema = object({
   timestamp: pipe(
      isoDateTimeSchema,
      description('When the weight was measured; identifies the entry.'),
   ),
   weight: optional(
      pipe(
         number(),
         description('Weight in kilograms. Required unless the entry is a tombstone.'),
      ),
   ),
   deletedAt: optional(
      pipe(
         nullable(isoDateTimeSchema),
         description('Set when the entry is a tombstone.'),
      ),
   ),
   updatedAt: pipe(
      isoDateTimeSchema,
      description('Last modification on the client; drives last-writer-wins.'),
   ),
})

export const weightListResponseSchema = object({
   ok: boolean(),
   entries: array(weightEntrySchema),
   serverTime: pipe(
      isoDateTimeSchema,
      description('Pass back as `since` on the next delta request.'),
   ),
})

export const weightUpsertRequestSchema = object({
   userId: hashedUserIdSchema,
   entries: pipe(
      array(weightEntrySchema),
      maxLength(
         MAX_WEIGHT_ENTRIES_PER_REQUEST,
         `entries may hold at most ${MAX_WEIGHT_ENTRIES_PER_REQUEST} items`,
      ),
   ),
})

export const weightUpsertOutcomeSchema = object({
   timestamp: pipe(
      nullable(string()),
      description('Null when the submitted entry had no usable timestamp.'),
   ),
   status: picklist(['applied', 'stale', 'invalid']),
   error: optional(
      pipe(string(), description('Validation message; present only for `invalid`.')),
   ),
})

export const weightUpsertResponseSchema = object({
   ok: boolean(),
   results: array(weightUpsertOutcomeSchema),
   entries: pipe(
      array(weightEntrySchema),
      description('The stored version of every valid submitted entry, winner or not.'),
   ),
   serverTime: isoDateTimeSchema,
   serverUpdatedAt: pipe(
      nullable(isoDateTimeSchema),
      description(
         'The new `/v1/sync` conflict stamp when anything was written, else null.',
      ),
   ),
   previousServerUpdatedAt: pipe(
      nullable(isoDateTimeSchema),
      description(
         'The stamp this write replaced. A client whose own stamp equals it adopts `serverUpdatedAt`, so its next `/v1/sync` is not a 409; any other value means another device changed something in between.',
      ),
   ),
})

export type WeightEntry = InferOutput<typeof weightEntrySchema>
export type WeightListResponse = InferOutput<typeof weightListResponseSchema>
export type WeightUpsertResponse = InferOutput<typeof weightUpsertResponseSchema>
